import { FC, useState } from 'react';
import { FloatingPanel } from '@mews/b2b-ui';
import { createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Stack, Typography } from '@optimus-web/core';
import { GroupsAndReservationsDialogFormSchema, GroupsAndReservationsDialogFormValues } from './schema';
import { GroupsAndReservationsDialogContent } from './GroupsAndReservationsDialogContent';
import { normalizeGroupsAndReservationsFormValues } from './utils';
import { GroupsAndReservationsDialogDefaultValues } from './types';
import { useDefaultFormValues } from './useDefaultFormValues';
import { useSearchGroupsAndReservations } from './useSearchGroupsAndReservations';

export interface GroupsAndReservationsDialogProps {
    onClose: () => void;
    active: boolean;
    onSubmit: (values: { reservationIds: string[]; reservationGroupIds: string[] }) => void;
    defaultValues?: GroupsAndReservationsDialogDefaultValues;
}
export const GroupsAndReservationsDialog: FC<GroupsAndReservationsDialogProps> = ({
    onClose,
    active,
    defaultValues,
    onSubmit,
}) => {
    const defaultFormValues = useDefaultFormValues(defaultValues);
    const { renderFormContent, submit, reset } = useFormControls<GroupsAndReservationsDialogFormValues>({
        resolver: createZodFormResolver(GroupsAndReservationsDialogFormSchema),
        onSubmit: (values: GroupsAndReservationsDialogFormValues) =>
            onSubmit(normalizeGroupsAndReservationsFormValues(values)),
        values: defaultFormValues,
    });

    const [searchToken, setSearchToken] = useState<string>('');
    const { groupsOptions, groupToReservationMap } = useSearchGroupsAndReservations(searchToken, defaultValues);

    const handleDialogClose = () => {
        onClose();
        reset();
    };

    return renderFormContent(
        <FloatingPanel
            onClose={handleDialogClose}
            active={active}
            title={lt(TextKey.RoutingRulesFormGroupsAndReservationsDialogTitle)}
            footer={{
                primary: (
                    <Button
                        variant="primary"
                        type="submit"
                        size="medium"
                        onClick={(e) => {
                            e.stopPropagation();
                            void submit();
                        }}
                        data-test-id="routing-rules-reservations-dialog-save"
                    >
                        {lt(TextKey.Save)}
                    </Button>
                ),
                secondary: (
                    <Button
                        variant="tertiary"
                        type="button"
                        size="medium"
                        onClick={handleDialogClose}
                        data-test-id="routing-rules-reservations-dialog-cancel"
                    >
                        {lt(TextKey.Cancel)}
                    </Button>
                ),
            }}
        >
            <Stack vertical spacing="200">
                <Typography variant="secondary" textStyle="bodyMedium" as="div">
                    {lt(TextKey.RoutingRulesFormGroupsAndReservationsDialogDescription)}
                </Typography>
                <GroupsAndReservationsDialogContent
                    searchToken={searchToken}
                    setSearchToken={setSearchToken}
                    groupsOptions={groupsOptions}
                    groupToReservationMap={groupToReservationMap}
                />
            </Stack>
        </FloatingPanel>
    );
};
